import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Download, Search, ChevronRight, X, Save, StickyNote, Phone, Mail, MapPin, Truck,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import RetailerLayout from "@/components/RetailerLayout";
import {
  SERVICE_LABEL, STATUS_OPTIONS, STATUS_STYLE, RADIUS_OPTIONS, FITTER_SERVICES, FITTER_SERVICE_CHOICES,
} from "@/lib/fitters";

export default function AdminFitters() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [radius, setRadius] = useState("");
  const [service, setService] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (status) params.status = status;
    if (radius) params.radius = radius;
    if (service) params.service = service;
    try {
      const r = await api.get("/admin/fitters", { params });
      setRows(r.data.fitters || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, status, radius, service]);

  useEffect(() => { load(); }, [status, radius, service]); // eslint-disable-line

  async function exportCsv() {
    try {
      const r = await api.get("/admin/fitters/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "fitters.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  }

  return (
    <RetailerLayout>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-fitters-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h1 className="font-display font-bold text-2xl text-slate-900">Fitters</h1>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md text-slate-700 hover:bg-slate-100 border border-slate-200" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input className="fm-input pl-9" placeholder="Name, postcode, town…" value={q}
                onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="fitter-search" />
            </div>
            <select className="fm-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status">
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
            </select>
            <select className="fm-input" value={radius} onChange={(e) => setRadius(e.target.value)} data-testid="filter-radius">
              <option value="">Any radius</option>
              {RADIUS_OPTIONS.map((r) => <option key={r} value={r}>{r} miles</option>)}
            </select>
            <select className="fm-input" value={service} onChange={(e) => setService(e.target.value)} data-testid="filter-service">
              <option value="">Any service</option>
              {FITTER_SERVICE_CHOICES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {/* List */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-slate-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-slate-500 text-center" data-testid="fitters-empty">No fitter applications match.</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="fitters-list">
              {rows.map((f) => (
                <li key={f.id}>
                  <button onClick={() => setSelected(f)} className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors" data-testid={`fitter-row-${f.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">
                        {f.full_name}{f.trading_name ? ` · ${f.trading_name}` : ""}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {f.reference_code} · {f.district || f.postcode} · {f.working_radius_miles}mi · {f.partner_type}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 capitalize ${STATUS_STYLE[f.status] || "bg-slate-100 text-slate-700"}`}>{f.status}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      {selected && (
        <FitterDetail
          fitter={selected}
          onClose={() => setSelected(null)}
          onSaved={(updated) => { setSelected(updated); load(); }}
        />
      )}
    </RetailerLayout>
  );
}

function FitterDetail({ fitter, onClose, onSaved }) {
  const [f, setF] = useState(fitter);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  async function patch(body) {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/fitters/${f.id}`, body);
      setF(r.data.fitter);
      setNote("");
      onSaved(r.data.fitter);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end" role="dialog" aria-modal="true" data-testid="fitter-detail-drawer" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-lg text-slate-900">{f.full_name}</h3>
            <p className="text-xs text-slate-500">{f.reference_code}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-fitter-detail"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Status */}
          <div>
            <span className="fm-label">Status</span>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="detail-status">
              {STATUS_OPTIONS.map((s) => (
                <button key={s} onClick={() => patch({ status: s })} disabled={saving}
                  className={`text-xs font-semibold rounded-full px-3 py-1.5 capitalize transition-colors ${f.status === s ? (STATUS_STYLE[s] || "bg-slate-200") + " ring-2 ring-offset-1 ring-slate-400" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  data-testid={`set-status-${s}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Contact (admin-only) */}
          <Section title="Contact (private)">
            <Line icon={Mail} value={f.email} />
            <Line icon={Phone} value={f.mobile} />
            <Line icon={MapPin} value={`${f.postcode}${f.district ? " · " + f.district : ""}`} />
            {f.website && <Line icon={Truck} value={f.website} />}
          </Section>

          {/* Details */}
          <Section title="Application">
            <KV k="Partner type" v={f.partner_type} />
            <KV k="Working radius" v={`${f.working_radius_miles} miles`} />
            <KV k="Areas" v={(f.areas_covered || []).join(", ") || "—"} />
            <KV k="Experience" v={`${f.years_experience} yrs`} />
            <KV k="Own vehicle" v={f.own_vehicle ? "Yes" : "No"} />
            <KV k="Can collect" v={f.can_collect ? "Yes" : "No"} />
            {f.vehicle_type && <KV k="Vehicle" v={f.vehicle_type} />}
            {f.max_roll_length_m != null && <KV k="Max roll length" v={`${f.max_roll_length_m} m`} />}
            <KV k="Multiple collections" v={f.multiple_collections ? "Yes" : "No"} />
            <KV k="Availability" v={(f.availability || []).join(", ") || "—"} />
            <KV k="Public liability" v={f.has_public_liability ? (f.public_liability_insurer || "Yes") : "No"} />
            {f.policy_expiry && <KV k="Policy expiry" v={f.policy_expiry} />}
            <div className="pt-1">
              <div className="text-xs text-slate-500 mb-1">Services</div>
              <div className="flex flex-wrap gap-1.5">
                {(f.services || []).map((s) => <span key={s} className="text-xs bg-slate-100 rounded-full px-2.5 py-1">{SERVICE_LABEL[s] || s}</span>)}
              </div>
            </div>
            {f.description && <p className="text-sm text-slate-600 pt-2">{f.description}</p>}
          </Section>

          {/* CRM */}
          <Section title="CRM">
            <div className="grid grid-cols-1 gap-3">
              <label className="block">
                <span className="fm-label">Source</span>
                <select className="fm-input" defaultValue={f.source || ""} onChange={(e) => patch({ source: e.target.value })} data-testid="crm-source">
                  <option value="">—</option>
                  {["google", "directory", "recommendation", "facebook", "direct"].map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="fm-label">Initial contact</span>
                  <input type="date" className="fm-input" defaultValue={f.initial_contact_date || ""} onBlur={(e) => e.target.value !== (f.initial_contact_date || "") && patch({ initial_contact_date: e.target.value })} data-testid="crm-initial-date" />
                </label>
                <label className="block">
                  <span className="fm-label">Follow-up</span>
                  <input type="date" className="fm-input" defaultValue={f.follow_up_date || ""} onBlur={(e) => e.target.value !== (f.follow_up_date || "") && patch({ follow_up_date: e.target.value })} data-testid="crm-followup-date" />
                </label>
              </div>
            </div>
          </Section>

          {/* Notes */}
          <Section title="Private notes">
            <div className="space-y-2 mb-3">
              {(f.admin_notes || []).length === 0 && <p className="text-sm text-slate-400">No notes yet.</p>}
              {(f.admin_notes || []).map((n, i) => (
                <div key={`${n.at || ""}-${i}`} className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                  <p className="text-slate-800">{n.text}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{n.by} · {new Date(n.at).toLocaleString("en-GB")}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input className="fm-input flex-1" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" />
              <button onClick={() => note.trim() && patch({ note })} disabled={saving || !note.trim()} className="h-11 px-4 rounded-xl bg-[#0F3D2E] text-white font-semibold inline-flex items-center gap-1 disabled:opacity-60" data-testid="add-note-btn">
                <StickyNote className="w-4 h-4" /> Add
              </button>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</h4>
      <div className="bg-white space-y-1.5">{children}</div>
    </section>
  );
}
function Line({ icon: Icon, value }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-800">
      <Icon className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" /> <span className="truncate">{value}</span>
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-slate-500">{k}</span>
      <span className="font-semibold text-slate-900 capitalize text-right">{v}</span>
    </div>
  );
}
